
import { Component, computed, effect, input, output, signal } from '@angular/core';
import { CheckboxComponent, CheckboxSelectionChangedEvent } from '../checkbox/checkbox.component';
import { RadioComponent, RadioSelectionChangedEvent } from '../radio/radio.component';
import { ButtonComponent } from '../button/button.component';
import { ButtonGroupComponent } from '../button-group/button-group.component';
import { TextboxComponent } from '../textbox/textbox.component';
import { StoreService } from '../../../store/store.service';
import { FiltersService } from '../../services/filters.service';
import { RouterService } from '../../services/router.service';
import { CommonService } from '../../services/common.service';
import { DEFAULT_ROOM_ATTRIBUTES_MAP } from '../../configs/common.config';
import { FormDropdownComponent } from '../form-dropdown/form-dropdown.component';
import { DayUseRoomsComponent } from '../../../app/pages/layout/day-use-rooms/day-use-rooms.component';

@Component({
  standalone: true,
  selector: 'app-filters',
  imports: [
    CheckboxComponent,
    RadioComponent,
    ButtonComponent,
    ButtonGroupComponent,
    TextboxComponent,
    FormDropdownComponent,
    DayUseRoomsComponent,
  ],
  templateUrl: './filters.component.html',
  styleUrl: './filters.component.scss',
  host: {
    '[class.overflow-hidden]': '!isCalendarSidepanel()',
  },
})
export class FiltersComponent {
  // Required inputs
  public filters = input.required<Record<string, any>>();

  // Optional inputs
  public readonly isCalendarSidepanel = input<boolean>(false);

  // Signals
  public selectedFilters = signal<Record<string, any>>({});
  public sortFilters = signal<any[]>([]);
  public searchTerm = signal<string>('');
  private isInitialized = false;

  // Computed Signals
  public readonly isMobileLarge = computed(() => {
    return (this.storeSvc.res()?.mobileLarge) ?? false;
  });

  constructor(
    public readonly storeSvc: StoreService,
    public readonly routerSvc: RouterService,
    private readonly filtersSvc: FiltersService,
    private readonly commonSvc: CommonService
  ) {
    // Watch for changes to filters input (only initialize once per component lifecycle)
    effect(() => {
      const currentFilters = this.filters();
      // Only initialize once when we first get valid filters data
      if (!this.isInitialized && currentFilters && Object.keys(currentFilters).length > 0) {
        this.initializeFiltersData();
        this.isInitialized = true;
      }
    });
  }

  public selectedFiltersAndSortFilters = output<Record<string, any>>();
  public closeModalEvent = output<void>();

  public btnGroup = [
    {
      label: this.storeSvc.localeObj()?.tf_2_RoomList_roomListFilters_cancel,
      icon: '',
      id: 'filters-cancel-btn',
      variant: 'secondary',
      iconDisplay: '',
      isLoading: false,
      isDisabled: false,
      groupPosition: 'left',
      connected: false,
      value: 'CANCEL',
    },
    {
      label: this.storeSvc.localeObj()?.tf_2_RoomList_roomListFilters_apply,
      icon: '',
      id: 'filters-apply-btn',
      variant: 'primary',
      iconDisplay: '',
      isLoading: false,
      isDisabled: false,
      groupPosition: 'right',
      connected: false,
      value: 'APPLY',
    },
  ];

  public filtersArray = computed(() => {
    const calendarAttributeCodes = this.storeSvc.propInfo()?.calendarAttributeCodes || [];
    const isSearchPage = this.routerSvc.isSearchPage || this.isCalendarSidepanel();
    const isRoomsPage = this.routerSvc.isRoomsPage && !this.isCalendarSidepanel();

    // Get the actual selected state from signals
    const currentSelectedFilters = this.selectedFilters();
    const currentSortFilters = this.sortFilters();

    // Build filters array from selectedFilters (which contains current state)
    // Keep track of filter keys for defaultAttribute lookup
    let filtersWithKeys: Array<{ filter: any; filterKey: string }> = Object.entries(currentSelectedFilters).map(
      ([key, filter]) => ({ filter, filterKey: key })
    );

    // Filter by calendarAttributeCodes ONLY on search page (not on rooms page)
    if (isSearchPage && !isRoomsPage && calendarAttributeCodes.length > 0) {
      filtersWithKeys = filtersWithKeys.filter(({ filterKey }) =>
        calendarAttributeCodes.includes(filterKey)
      );
    }

    const searchTermLower = this.searchTerm()?.toLowerCase();

    // Process sort filters (if any) - exclude on search page
    const processedSortFilters = isSearchPage && !isRoomsPage
      ? []
      : currentSortFilters
          .filter(filter =>
            searchTermLower === '' ||
            filter.attributeName.toLowerCase().includes(searchTermLower) ||
            Object.values(filter.options).some((option: any) =>
              option.toString().toLowerCase().includes(searchTermLower)
            )
          )
          .map((filter) => ({
            attributeName: filter.attributeName,
            selectionType: filter.selectionType,
            defaultAttribute: false,
            filterKey: null,
            options: Object.entries(filter.options).map(([id, label]) => ({
              id,
              label,
              selected: filter.selectedOptions?.[id] ?? false
            })),
          }));

    // Process attribute filters
    const processedAttributeFilters = filtersWithKeys
      .filter(({ filter }) =>
        searchTermLower === '' ||
        filter.attributeName.toLowerCase().includes(searchTermLower) ||
        Object.values(filter.options).some((option: any) =>
          option.toString().toLowerCase().includes(searchTermLower)
        )
      )
      .map(({ filter, filterKey }) => ({
        attributeName: filter.attributeName,
        selectionType: filter.selectionType,
        defaultAttribute: filter.defaultAttribute || false,
        filterKey: filterKey,
        options: Object.entries(filter.options).map(([id, label]) => ({
          id,
          key: id, // for form dropdown
          label,
          value: label, // for form dropdown
          selected: filter.selectedOptions?.[id] ?? false,
        })),
        selectedOptionIds: (
          filter.selectionType === 'multiple'
            ? Object.entries(filter.selectedOptions)
                .filter(([argIgnore_id, selected]) => selected)
                .map(([id]) => id)
            : Object.entries(filter.selectedOptions)
                .find(([argIgnore_id, selected]) => selected)?.[0] || ''
        )
      }));

    // Combine sort filters and attribute filters
    return [...processedSortFilters, ...processedAttributeFilters];
  });

  private initializeFiltersData(): void {
    // Initialize filters using the service
    const initialized = this.filtersSvc.initializeFilters(this.filters());
    this.selectedFilters.set(initialized.selectedFilters);
    this.sortFilters.set(initialized.sortFilters);
  }

  public resetFilters = () => {
    // Clear all filters to default state (Reset button)
    const rawFilters = JSON.parse(JSON.stringify(this.filters()));

    // Force all selectedOptions to false
    Object.keys(rawFilters).forEach(key => {
      const selectedOptions: Record<string, boolean> = {};
      Object.keys(rawFilters[key].options || {}).forEach(optionId => {
        selectedOptions[optionId] = false;
      });
      rawFilters[key].selectedOptions = selectedOptions;
    });

    this.selectedFilters.set(rawFilters);

    // Reset sort filters to default
    const defaultSortFilters = this.filtersSvc['getDefaultSortFilters']();
    const resetSortFilters = defaultSortFilters.map((filter: any) => {
      const newSelectedOptions: Record<string, boolean> = {};
      Object.keys(filter.selectedOptions).forEach(key => {
        if (filter.attributeName.includes(this.storeSvc.localeObj()?.tf_2_RoomList_roomListFilters_sortResults)
          || filter.attributeName.includes(this.storeSvc.localeObj()?.tf_2_RoomList_roomListFilters_groupResults)) {
          newSelectedOptions[key] = key === '0';
        } else if (filter.attributeName.includes(this.storeSvc.localeObj()?.tf_2_RoomList_FeeInclusive)) {
          // Reset based on propertyInfo value
          const propertyInfoValue = this.storeSvc.propInfo()?.showTaxesInDisplayPrice ?? false;
          newSelectedOptions[key] = (key === '0' && propertyInfoValue) ||
            (key === '1' && !propertyInfoValue);
        } else {
          newSelectedOptions[key] = key === '1';
        }
      });
      return { ...filter, selectedOptions: newSelectedOptions };
    });
    this.sortFilters.set(resetSortFilters);
  };

  public cancelFilters = () => {
    // Revert to last applied state (Cancel button)
    const restored = this.filtersSvc.resetToSnapshot();
    this.selectedFilters.set(restored.selectedFilters);
    this.sortFilters.set(restored.sortFilters);
  };

  public handleBtnClick = (e: any) => {
    if (e === 'CANCEL') {
      this.cancelFilters();
    } else if (e === 'APPLY') {
      this.applyFilters();
    }
    this.closeModalEvent.emit();
  };

  public applyFilters = () => {
    this.selectedFiltersAndSortFilters.emit({
      filters: this.selectedFilters(),
      sortFilters: this.sortFilters(),
    });
  };

  public handleDropdownSelectionChanged = (filter: any, selection: string | string[]) => {
    console.log('handleDropdownSelectionChanged', filter, selection);
    const updatedFilterOptions = filter.options.map((option: any) => ({
        ...option,
        selected: filter.selectionType === 'multiple'
          ? selection.includes(option.key)
          : selection === option.key
      })
    );
    filter.options = updatedFilterOptions;
    this.updateFilters(filter, 'dropdown');
    this.applyFilters();
  };

  public handleSelectionChanged = (e: CheckboxSelectionChangedEvent | RadioSelectionChangedEvent, type: any) => {
    if (!e.isInitialSelection) {
      this.updateFilters(e, type);
    }
  };

  private updateFilters(selection: any[] | any, type: string) {
    // Use current selectedFilters state, not raw API filters
    const currentFilters = this.selectedFilters();
    let filterKey = '';
    if (type === 'radio') {
      const attributeName = selection.rname;
      filterKey = Object.keys(currentFilters).find((key) =>
        currentFilters[key].attributeName === attributeName) ?? '';
    } else if (type === 'dropdown') {
      filterKey = Object
        .keys(currentFilters)
        .find(
          (key) => currentFilters[key].attributeName === selection.attributeName
        ) ?? '';
    } else {
      filterKey = Object.keys(currentFilters).find((key) =>
        currentFilters[key].attributeName === selection.options[0].cname) ?? '';
    }
    if (filterKey) {
      // Create new filter object instead of mutating
      const updatedFilters = { ...currentFilters };
      const filter = { ...updatedFilters[filterKey] };
      const selectedOptions = { ...(filter.selectedOptions || {}) };

      // Update all options based on the selection
      if (type === 'radio') {
        // For radio buttons, clear all options first, then set only the selected one
        Object.keys(filter.options).forEach(key => {
          selectedOptions[key] = false;
        });

        const optionKey = Object.keys(filter.options).find((key) => filter.options[key] === selection.label);
        if (optionKey) {
          selectedOptions[optionKey] = selection.selected;
        }
      } else {
        selection.options.forEach((option: any) => {
          const optionKey = Object.keys(filter.options).find((key) => filter.options[key] === option.label);
          if (optionKey) {
            selectedOptions[optionKey] = option.selected;
          }
        });
      }

      filter.selectedOptions = selectedOptions;
      updatedFilters[filterKey] = filter;
      this.selectedFilters.set(updatedFilters);
    } else {
      // For sort filters, use the service to update
      const attributeName = selection.rname;
      const updatedSortFilters = this.filtersSvc.updateSortFilter(
        this.sortFilters(),
        attributeName,
        selection.id,
        selection.selected
      );
      this.sortFilters.set(updatedSortFilters);
    }
  }

  public handleSearch = (e: string) => {
    if(e !== undefined) this.searchTerm.set(e);
  };

  /**
   * Returns the translated default room attribute name.
   *
   * @param filter - The filter object containing filterKey and defaultAttribute properties.
   * @returns The translated room attribute name in upper case if propertyInfo.displayUpperCaseLabels is true,
   *  otherwise in its original case.
   */
  public getTranslatedDefRoomAttrName(filter: any): string {
    const filterKey = filter?.filterKey;
    if (!filterKey || !filter?.defaultAttribute) {
      return filter?.attributeName || '';
    }

    const localeKey = DEFAULT_ROOM_ATTRIBUTES_MAP.get(filterKey);
    const translatedName = this.storeSvc.localeObj()?.[localeKey || ''] || filter.attributeName;
    const isUpperCaseLabel = this.storeSvc.propInfo()?.displayUpperCaseLabels ?? false;

    return this.commonSvc.getUpperCaseString(isUpperCaseLabel, translatedName);
  }
}
